import DownloadIcon from '@mui/icons-material/Download';
import {
    Box,
    Button,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography
} from '@mui/material';
import { useEffect, useState } from 'react';
import { API_URL } from '../config/api';

function VisitsReportPanel({ token }) {
    const [reportData, setReportData] = useState([]);
    const [startDate, setStartDate] = useState(
        new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0]
    );
    const [endDate, setEndDate] = useState(
        new Date().toISOString().split('T')[0]
    );
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        fetchReport();
    }, []);

    const fetchReport = async () => {
        setLoading(true);
        try {
            const response = await fetch(
                `${API_URL}/api/visits/report?startDate=${startDate}&endDate=${endDate}`,
                {
                    headers: token ? { Authorization: `Bearer ${token}` } : {}
                }
            );
            const data = await response.json();
            if (data.success) {
                setReportData(data.report || []);
            } else {
                setReportData([]);
            }
        } catch (error) {
            console.error('Error fetching report:', error);
        } finally {
            setLoading(false);
        }
    };

    const getDayOfWeek = (dateStr) => {
        const days = ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'];
        const date = new Date(dateStr);
        return days[date.getDay()];
    };

    const exportToCSV = () => {
        const headers = [
            'Дата',
            'День недели',
            'Кол-во гостей',
            'Интернет',
            'С улицы',
            'Яндекс',
            'Спутник',
            '% с улицы',
            'Число гостей со статусом "были"',
            'Число гостей со статусом "отмена"'
        ];

        const rows = reportData.map(row => [
            new Date(row.date).toLocaleDateString('ru-RU'),
            getDayOfWeek(row.date),
            row.total_guests,
            row.internet,
            row.walk_in,
            row.yandex,
            row.sputnik,
            row.walk_in_percent + '%',
            row.status_attended,
            row.status_cancelled
        ]);

        const csv = [
            headers.join(','),
            ...rows.map(row => row.join(','))
        ].join('\n');

        const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `Отчет_посещения_${startDate}_${endDate}.csv`;
        link.click();
    };

    const totals = reportData.reduce((acc, row) => ({
        total_guests: acc.total_guests + row.total_guests,
        internet: acc.internet + row.internet,
        walk_in: acc.walk_in + row.walk_in,
        yandex: acc.yandex + row.yandex,
        sputnik: acc.sputnik + row.sputnik,
        status_attended: acc.status_attended + row.status_attended,
        status_cancelled: acc.status_cancelled + row.status_cancelled
    }), {
        total_guests: 0,
        internet: 0,
        walk_in: 0,
        yandex: 0,
        sputnik: 0,
        status_attended: 0,
        status_cancelled: 0
    });

    const avgWalkInPercent = totals.total_guests > 0
        ? ((totals.walk_in / totals.total_guests) * 100).toFixed(1)
        : 0;

    return (
        <Box>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
                <Typography variant="h5" fontWeight="bold">
                    📊 Детальный отчет по посещениям
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<DownloadIcon />}
                    onClick={exportToCSV}
                    disabled={reportData.length === 0}
                >
                    Экспорт CSV
                </Button>
            </Box>

            <Paper sx={{ p: 2, mb: 3 }}>
                <Box display="flex" gap={2} alignItems="center">
                    <TextField
                        type="date"
                        label="Начало периода"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                        type="date"
                        label="Конец периода"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                    />
                    <Button
                        variant="contained"
                        onClick={fetchReport}
                        disabled={loading}
                    >
                        {loading ? 'Загрузка...' : 'Обновить'}
                    </Button>
                </Box>
            </Paper>

            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow sx={{ bgcolor: 'primary.main' }}>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Дата</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>День недели</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="right">Кол-во гостей</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="right">Интернет</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="right">С улицы</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="right">Яндекс</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="right">Спутник</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="right">% с улицы</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="right">Были</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="right">Отмена</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={10} align="center">
                                    Загрузка данных...
                                </TableCell>
                            </TableRow>
                        ) : reportData.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={10} align="center">
                                    Нет данных за выбранный период
                                </TableCell>
                            </TableRow>
                        ) : (
                            <>
                                {reportData.map((row, index) => (
                                    <TableRow key={index} hover>
                                        <TableCell>{new Date(row.date).toLocaleDateString('ru-RU')}</TableCell>
                                        <TableCell>{getDayOfWeek(row.date)}</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>{row.total_guests}</TableCell>
                                        <TableCell align="right">{row.internet}</TableCell>
                                        <TableCell align="right">{row.walk_in}</TableCell>
                                        <TableCell align="right">{row.yandex}</TableCell>
                                        <TableCell align="right">{row.sputnik}</TableCell>
                                        <TableCell align="right">{row.walk_in_percent}%</TableCell>
                                        <TableCell align="right" sx={{ color: 'success.main' }}>{row.status_attended}</TableCell>
                                        <TableCell align="right" sx={{ color: 'error.main' }}>{row.status_cancelled}</TableCell>
                                    </TableRow>
                                ))}
                                {/* Итоговая строка */}
                                <TableRow sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold' }}>
                                    <TableCell colSpan={2} sx={{ fontWeight: 'bold' }}>ИТОГО:</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>{totals.total_guests}</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>{totals.internet}</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>{totals.walk_in}</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>{totals.yandex}</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>{totals.sputnik}</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>{avgWalkInPercent}%</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 'bold', color: 'success.main' }}>{totals.status_attended}</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 'bold', color: 'error.main' }}>{totals.status_cancelled}</TableCell>
                                </TableRow>
                            </>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
}

export default VisitsReportPanel;
